import React from 'react';
import { Checkbox, Input, Card, Divider } from 'antd';
import { ClientFeatures } from '../types';
import FormInput from './FormInput';
import VoiceProviderInput from './VoiceProviderInput';
import { VOICE_PROVIDERS } from '../constants';

interface ClientFeaturesFormProps {
	formData: any;
	clientFeatures: ClientFeatures;
	updateFeatures: (updates: Partial<ClientFeatures>) => void;
	updateVoiceProviderPricing: (provider: keyof typeof VOICE_PROVIDERS, value: number) => void;
	getFeatureValue: (path: string[]) => any;
	updateFeatureValue: (path: string[], value: any, useGatedValues?: boolean) => void;
	setFormData: (updates: any) => void;
}

const ClientFeaturesForm: React.FC<ClientFeaturesFormProps> = ({
	formData,
	clientFeatures,
	updateFeatures,
	updateVoiceProviderPricing,
	getFeatureValue,
	updateFeatureValue,
	setFormData,
}) => {
	const handleAdvancedCapabilityChange = (key: string, checked: boolean): void => {
		const updatedFeatures = {
			...formData.features,
		} as ClientFeatures;
		(
			updatedFeatures.advancedCapabilities[
				key as keyof typeof updatedFeatures.advancedCapabilities
			] as any
		).gatedValues[0].value = checked;
		setFormData({ ...formData, features: updatedFeatures });
	};

	const handleViewAccessUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(
			['advancedCapabilities', 'teamAccess', 'viewAccessMembers'],
			checked ? -1 : 0,
		);
	};

	const handleViewAccessInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['advancedCapabilities', 'teamAccess', 'viewAccessMembers'], value);
	};

	const handleEditAccessUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(
			['advancedCapabilities', 'teamAccess', 'editAccessMembers'],
			checked ? -1 : 0,
		);
	};

	const handleEditAccessInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['advancedCapabilities', 'teamAccess', 'editAccessMembers'], value);
	};

	const handleAnalyticsFeatureChange = (key: string, checked: boolean): void => {
		const updatedFeatures = {
			...formData.features,
		} as ClientFeatures;
		(
			updatedFeatures.AnalyticsAndReporting[
				key as keyof typeof updatedFeatures.AnalyticsAndReporting
			] as any
		).gatedValues[0].value = checked;
		setFormData({ ...formData, features: updatedFeatures });
	};

	const handleLogsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['AnalyticsAndReporting', 'logs'], checked ? -1 : 1);
	};

	const handleLogsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['AnalyticsAndReporting', 'logs'], value);
	};

	const handleMaxNotificationRecipientsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['AnalyticsAndReporting', 'maxNotificationRecipients'], checked ? -1 : 1);
	};

	const handleMaxNotificationRecipientsInputChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['AnalyticsAndReporting', 'maxNotificationRecipients'], value);
	};

	const handleCommunicationFeatureChange = (key: string, checked: boolean): void => {
		const updatedFeatures = {
			...formData.features,
		} as ClientFeatures;
		(
			updatedFeatures.communicationAndCallHandling[
				key as keyof typeof updatedFeatures.communicationAndCallHandling
			] as any
		).gatedValues[0].value = checked;
		setFormData({ ...formData, features: updatedFeatures });
	};

	const handleMinutesIncludedChange = (value: any): void => {
		const updatedFeatures = { ...formData.features } as ClientFeatures;
		updatedFeatures.communicationAndCallHandling.minutesIncluded.value = value as number;
		updateFeatures(updatedFeatures);
	};

	const handleSMSCreditsIncludedChange = (value: any): void => {
		const updatedFeatures = { ...formData.features } as ClientFeatures;
		updatedFeatures.communicationAndCallHandling.smsCreditsIncluded.value = value as number;
		updateFeatures(updatedFeatures);
	};

	const handleKnowledgeFeatureChange = (key: string, checked: boolean): void => {
		const updatedFeatures = {
			...formData.features,
		} as ClientFeatures;
		(
			updatedFeatures.knowledgeAndCRM[key as keyof typeof updatedFeatures.knowledgeAndCRM] as any
		).gatedValues[0].value = checked;
		setFormData({ ...formData, features: updatedFeatures });
	};

	const handleKnowledgeBaseLengthChange = (value: any): void => {
		updateFeatureValue(['knowledgeAndCRM', 'knowledgeBaseLengthPages'], value as number);
	};

	const handlePhoneNumberFeatureChange = (key: string, checked: boolean): void => {
		const updatedFeatures = {
			...formData.features,
		} as ClientFeatures;
		(
			updatedFeatures.phoneNumbersAndForwarding[
				key as keyof typeof updatedFeatures.phoneNumbersAndForwarding
			] as any
		).gatedValues[0].value = checked;
		setFormData({ ...formData, features: updatedFeatures });
	};

	const handleSupportCallsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['supportAndSLA', 'supportCalls'], checked ? -1 : 1);
	};

	const handleSupportCallsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['supportAndSLA', 'supportCalls'], value);
	};

	const handleSupportSLAChange = (value: any): void => {
		updateFeatureValue(['supportAndSLA', 'supportSLAWithInHours'], value as number);
	};

	const handleCustomWebhooksChange = (checked: boolean): void => {
		updateFeatureValue(['webhooks', 'customWebhooks'], checked ? -1 : false);
	};

	const handleCustomWebhooksUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['webhooks', 'customWebhooks'], checked ? -1 : 1);
	};

	const handleCustomWebhooksInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['webhooks', 'customWebhooks'], value);
	};

	const handleApiWorkflowsChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'apiWorkflows'], checked ? -1 : false);
	};

	const handleApiWorkflowsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'apiWorkflows'], checked ? -1 : 1);
	};

	const handleApiWorkflowsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'apiWorkflows'], value);
	};

	const handleCallTransferWorkflowsChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'callTransferWorkflows'], checked ? -1 : false);
	};

	const handleCallTransferWorkflowsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'callTransferWorkflows'], checked ? -1 : 1);
	};

	const handleCallTransferWorkflowsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'callTransferWorkflows'], value);
	};

	const handleIntakeFormWorkflowsChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'intakeFormWorkflows'], checked ? -1 : false);
	};

	const handleIntakeFormWorkflowsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'intakeFormWorkflows'], checked ? -1 : 1);
	};

	const handleIntakeFormWorkflowsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'intakeFormWorkflows'], value);
	};

	const handleOverThePhoneBookWorkflowsChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'overThePhoneBookWorkflows'], checked ? -1 : false);
	};

	const handleOverThePhoneBookWorkflowsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'overThePhoneBookWorkflows'], checked ? -1 : 1);
	};

	const handleOverThePhoneBookWorkflowsInputChange = (
		e: React.ChangeEvent<HTMLInputElement>,
	): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'overThePhoneBookWorkflows'], value);
	};

	const handlePostCallMessageChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'postCallMessage'], checked ? -1 : false);
	};

	const handlePostCallMessageUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'postCallMessage'], checked ? -1 : 1);
	};

	const handlePostCallMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'postCallMessage'], value);
	};

	const handleTextWorkflowsChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'textWorkflows'], checked ? -1 : false);
	};

	const handleTextWorkflowsUnlimitedChange = (checked: boolean): void => {
		updateFeatureValue(['workflows', 'textWorkflows'], checked ? -1 : 1);
	};

	const handleTextWorkflowsInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
		updateFeatureValue(['workflows', 'textWorkflows'], value);
	};

	const getInputValue = (path: string[]): string | number => {
		const val = getFeatureValue(path);
		return typeof val === 'number' ? val : '';
	};

	const renderAdvancedCapabilitiesCheckboxes = (): JSX.Element[] => {
		return Object.entries(clientFeatures?.advancedCapabilities || {})
			.filter(([key]) => key !== 'teamAccess')
			.map(([key, feature]) => {
				const typedFeature = feature as {
					displayText: string;
					gatedValues: { effectiveAfter: number; value: boolean }[];
					tooltip: string;
				};
				return (
					<Checkbox
						key={key}
						id={key}
						checked={typedFeature.gatedValues[0].value}
						onChange={(e) => handleAdvancedCapabilityChange(key, e.target.checked)}
					>
						<span className="text-sm text-gray-900">{typedFeature.displayText}</span>
					</Checkbox>
				);
			});
	};

	const renderAnalyticsCheckboxes = (): JSX.Element[] => {
		return Object.entries(clientFeatures?.AnalyticsAndReporting || {})
			.filter(([key]) => key !== 'logs' && key !== 'maxNotificationRecipients')
			.map(([key, feature]) => {
				const typedFeature = feature as {
					displayText: string;
					gatedValues: { effectiveAfter: number; value: boolean }[];
					tooltip: string;
				};
				return (
					<Checkbox
						key={key}
						id={key}
						checked={typedFeature.gatedValues[0].value}
						onChange={(e) => handleAnalyticsFeatureChange(key, e.target.checked)}
					>
						<span className="text-sm text-gray-900">{typedFeature.displayText}</span>
					</Checkbox>
				);
			});
	};

	const renderCommunicationCheckboxes = (): JSX.Element[] => {
		return Object.entries(clientFeatures?.communicationAndCallHandling || {})
			.filter(
				([key]) => !['minutesIncluded', 'smsCreditsIncluded', 'overageVoiceCharges'].includes(key),
			)
			.map(([key, feature]) => {
				const typedFeature = feature as {
					displayText: string;
					gatedValues: { effectiveAfter: number; value: boolean }[];
					tooltip: string;
				};
				return (
					<Checkbox
						key={key}
						id={key}
						checked={typedFeature.gatedValues[0].value}
						onChange={(e) => handleCommunicationFeatureChange(key, e.target.checked)}
					>
						<span className="text-sm text-gray-900">{typedFeature.displayText}</span>
					</Checkbox>
				);
			});
	};

	const renderKnowledgeCheckboxes = (): JSX.Element[] => {
		return Object.entries(clientFeatures?.knowledgeAndCRM || {})
			.filter(([key]) => key !== 'knowledgeBaseLengthPages')
			.map(([key, feature]) => {
				const typedFeature = feature as {
					displayText: string;
					gatedValues: { effectiveAfter: number; value: boolean }[];
					tooltip: string;
				};
				return (
					<Checkbox
						key={key}
						id={key}
						checked={typedFeature.gatedValues[0].value}
						onChange={(e) => handleKnowledgeFeatureChange(key, e.target.checked)}
					>
						<span className="text-sm text-gray-900">{typedFeature.displayText}</span>
					</Checkbox>
				);
			});
	};

	const renderPhoneNumberCheckboxes = (): JSX.Element[] => {
		return Object.entries(clientFeatures?.phoneNumbersAndForwarding || {}).map(([key, feature]) => {
			const typedFeature = feature as {
				displayText: string;
				gatedValues: { effectiveAfter: number; value: boolean }[];
				tooltip: string;
			};
			return (
				<Checkbox
					key={key}
					id={key}
					checked={typedFeature.gatedValues[0].value}
					onChange={(e) => handlePhoneNumberFeatureChange(key, e.target.checked)}
				>
					<span className="text-sm text-gray-900">{typedFeature.displayText}</span>
				</Checkbox>
			);
		});
	};

	return (
		<div className="space-y-6">
			<Card title="Advanced Capabilities" className="border rounded-lg">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{renderAdvancedCapabilitiesCheckboxes()}
					</div>

					<Divider orientation="left" className="text-md font-medium text-gray-700">
						Team Access
					</Divider>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-2">
							View Access Members
						</label>
						<div className="space-y-2">
							<Checkbox
								id="viewAccessUnlimited"
								checked={
									getFeatureValue(['advancedCapabilities', 'teamAccess', 'viewAccessMembers']) ===
									-1
								}
								onChange={(e) => handleViewAccessUnlimitedChange(e.target.checked)}
							>
								<span className="text-sm font-medium text-gray-700">Unlimited</span>
							</Checkbox>
							{getFeatureValue(['advancedCapabilities', 'teamAccess', 'viewAccessMembers']) ===
							-1 ? (
								<div className="text-sm text-gray-600 font-medium">Unlimited</div>
							) : (
								<Input
									type="number"
									min="0"
									value={getInputValue(['advancedCapabilities', 'teamAccess', 'viewAccessMembers'])}
									onChange={handleViewAccessInputChange}
									placeholder="Enter number"
									className="w-full"
								/>
							)}
						</div>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Edit Access Members
						</label>
						<div className="space-y-2">
							<Checkbox
								id="editAccessUnlimited"
								checked={
									getFeatureValue(['advancedCapabilities', 'teamAccess', 'editAccessMembers']) ===
									-1
								}
								onChange={(e) => handleEditAccessUnlimitedChange(e.target.checked)}
							>
								<span className="text-sm font-medium text-gray-700">Unlimited</span>
							</Checkbox>
							{getFeatureValue(['advancedCapabilities', 'teamAccess', 'editAccessMembers']) ===
							-1 ? (
								<div className="text-sm text-gray-600 font-medium">Unlimited</div>
							) : (
								<Input
									type="number"
									min="0"
									value={getInputValue(['advancedCapabilities', 'teamAccess', 'editAccessMembers'])}
									onChange={handleEditAccessInputChange}
									placeholder="Enter number"
									className="w-full"
								/>
							)}
						</div>
					</div>
				</div>
			</Card>

			<Card title="Analytics & Reporting" className="border rounded-lg">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">{renderAnalyticsCheckboxes()}</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">Logs</label>
						<div className="space-y-2">
							<Checkbox
								id="logsUnlimited"
								checked={getFeatureValue(['AnalyticsAndReporting', 'logs']) === -1}
								onChange={(e) => handleLogsUnlimitedChange(e.target.checked)}
							>
								<span className="text-sm font-medium text-gray-700">Unlimited</span>
							</Checkbox>
							{getFeatureValue(['AnalyticsAndReporting', 'logs']) === -1 ? (
								<div className="text-sm text-gray-600 font-medium">Unlimited</div>
							) : (
								<Input
									type="number"
									min="1"
									value={getInputValue(['AnalyticsAndReporting', 'logs'])}
									onChange={handleLogsInputChange}
									placeholder="Enter number"
									className="w-full"
								/>
							)}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Max Notification Recipients
						</label>
						<div className="space-y-2">
							<Checkbox
								id="maxNotificationRecipientsUnlimited"
								checked={
									getFeatureValue(['AnalyticsAndReporting', 'maxNotificationRecipients']) === -1
								}
								onChange={(e) => handleMaxNotificationRecipientsUnlimitedChange(e.target.checked)}
							>
								<span className="text-sm font-medium text-gray-700">Unlimited</span>
							</Checkbox>
							{getFeatureValue(['AnalyticsAndReporting', 'maxNotificationRecipients']) === -1 ? (
								<div className="text-sm text-gray-600 font-medium">Unlimited</div>
							) : (
								<Input
									type="number"
									min="1"
									value={getInputValue(['AnalyticsAndReporting', 'maxNotificationRecipients'])}
									onChange={handleMaxNotificationRecipientsInputChange}
									placeholder="Enter number"
									className="w-full"
								/>
							)}
						</div>
					</div>
				</div>
			</Card>

			<Card title="Communication & Call Handling" className="border rounded-lg">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{renderCommunicationCheckboxes()}
					</div>

					<FormInput
						label="Minutes Included"
						value={clientFeatures?.communicationAndCallHandling?.minutesIncluded?.value || 300}
						onChange={handleMinutesIncludedChange}
						type="number"
					/>
					<FormInput
						label="SMS Credits Included"
						value={clientFeatures?.communicationAndCallHandling?.smsCreditsIncluded?.value || 83}
						onChange={handleSMSCreditsIncludedChange}
						type="number"
					/>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Voice Provider Pricing (Per Minute)
						</label>
						<div className="grid grid-cols-2 gap-4">
							{Object.entries(VOICE_PROVIDERS).map(([provider]) => (
								<VoiceProviderInput
									key={provider}
									provider={provider as keyof typeof VOICE_PROVIDERS}
									value={
										clientFeatures?.communicationAndCallHandling?.overageVoiceCharges?.charges?.[
											provider as keyof typeof VOICE_PROVIDERS
										] || (provider === 'transferredCall' ? 0.03 : 0.12)
									}
									onChange={(value) =>
										updateVoiceProviderPricing(provider as keyof typeof VOICE_PROVIDERS, value)
									}
								/>
							))}
						</div>
					</div>
				</div>
			</Card>

			<Card title="Knowledge & CRM" className="border rounded-lg">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">{renderKnowledgeCheckboxes()}</div>

					<FormInput
						label="Knowledge Base Length (Pages)"
						value={getFeatureValue(['knowledgeAndCRM', 'knowledgeBaseLengthPages']) || 20}
						onChange={handleKnowledgeBaseLengthChange}
						type="number"
					/>
				</div>
			</Card>

			<Card title="Phone Numbers & Forwarding" className="border rounded-lg">
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{renderPhoneNumberCheckboxes()}
					</div>
				</div>
			</Card>

			<Card title="Support & SLA" className="border rounded-lg">
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">Support Calls</label>
						<div className="space-y-2">
							<Checkbox
								id="supportCallsUnlimited"
								checked={getFeatureValue(['supportAndSLA', 'supportCalls']) === -1}
								onChange={(e) => handleSupportCallsUnlimitedChange(e.target.checked)}
							>
								<span className="text-sm font-medium text-gray-700">Unlimited</span>
							</Checkbox>
							{getFeatureValue(['supportAndSLA', 'supportCalls']) === -1 ? (
								<div className="text-sm text-gray-600 font-medium">Unlimited</div>
							) : (
								<Input
									type="number"
									min="1"
									value={getInputValue(['supportAndSLA', 'supportCalls'])}
									onChange={handleSupportCallsInputChange}
									placeholder="Enter number"
									className="w-full"
								/>
							)}
						</div>
					</div>

					<FormInput
						label="Support SLA (Hours)"
						value={getFeatureValue(['supportAndSLA', 'supportSLAWithInHours']) || 48}
						onChange={handleSupportSLAChange}
						type="number"
					/>
				</div>
			</Card>

			<Card title="Webhooks" className="border rounded-lg">
				<div className="space-y-4">
					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">Custom Webhooks</label>
							<Checkbox
								checked={getFeatureValue(['webhooks', 'customWebhooks']) !== false}
								onChange={(e) => handleCustomWebhooksChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.webhooks?.customWebhooks?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="customWebhooksUnlimited"
										checked={getFeatureValue(['webhooks', 'customWebhooks']) === -1}
										onChange={(e) => handleCustomWebhooksUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['webhooks', 'customWebhooks']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['webhooks', 'customWebhooks'])}
											onChange={handleCustomWebhooksInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>
				</div>
			</Card>

			<Card title="Workflows" className="border rounded-lg">
				<div className="space-y-6">
					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">API Workflows</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'apiWorkflows']) !== false}
								onChange={(e) => handleApiWorkflowsChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.apiWorkflows?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="apiWorkflowsUnlimited"
										checked={getFeatureValue(['workflows', 'apiWorkflows']) === -1}
										onChange={(e) => handleApiWorkflowsUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'apiWorkflows']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'apiWorkflows'])}
											onChange={handleApiWorkflowsInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">Call Transfer Workflows</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'callTransferWorkflows']) !== false}
								onChange={(e) => handleCallTransferWorkflowsChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.callTransferWorkflows?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="callTransferWorkflowsUnlimited"
										checked={getFeatureValue(['workflows', 'callTransferWorkflows']) === -1}
										onChange={(e) => handleCallTransferWorkflowsUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'callTransferWorkflows']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'callTransferWorkflows'])}
											onChange={handleCallTransferWorkflowsInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">Intake Form Workflows</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'intakeFormWorkflows']) !== false}
								onChange={(e) => handleIntakeFormWorkflowsChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.intakeFormWorkflows?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="intakeFormWorkflowsUnlimited"
										checked={getFeatureValue(['workflows', 'intakeFormWorkflows']) === -1}
										onChange={(e) => handleIntakeFormWorkflowsUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'intakeFormWorkflows']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'intakeFormWorkflows'])}
											onChange={handleIntakeFormWorkflowsInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">
								Over-the-Phone Booking Workflows
							</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'overThePhoneBookWorkflows']) !== false}
								onChange={(e) => handleOverThePhoneBookWorkflowsChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.overThePhoneBookWorkflows?.gatedValues[0].value !==
							false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="overThePhoneBookWorkflowsUnlimited"
										checked={getFeatureValue(['workflows', 'overThePhoneBookWorkflows']) === -1}
										onChange={(e) =>
											handleOverThePhoneBookWorkflowsUnlimitedChange(e.target.checked)
										}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'overThePhoneBookWorkflows']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'overThePhoneBookWorkflows'])}
											onChange={handleOverThePhoneBookWorkflowsInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">Post Call Message</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'postCallMessage']) !== false}
								onChange={(e) => handlePostCallMessageChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.postCallMessage?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="postCallMessageUnlimited"
										checked={getFeatureValue(['workflows', 'postCallMessage']) === -1}
										onChange={(e) => handlePostCallMessageUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'postCallMessage']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'postCallMessage'])}
											onChange={handlePostCallMessageInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>

					<Card size="small" className="border rounded-lg">
						<div className="flex items-center justify-between mb-3">
							<label className="text-sm font-medium text-gray-700">Text Workflows</label>
							<Checkbox
								checked={getFeatureValue(['workflows', 'textWorkflows']) !== false}
								onChange={(e) => handleTextWorkflowsChange(e.target.checked)}
							/>
						</div>
						{clientFeatures?.workflows?.textWorkflows?.gatedValues[0].value !== false && (
							<div className="space-y-2">
								<label className="block text-xs text-gray-600">Limit</label>
								<div className="space-y-2">
									<Checkbox
										id="textWorkflowsUnlimited"
										checked={getFeatureValue(['workflows', 'textWorkflows']) === -1}
										onChange={(e) => handleTextWorkflowsUnlimitedChange(e.target.checked)}
									>
										<span className="text-sm font-medium text-gray-700">Unlimited</span>
									</Checkbox>
									{getFeatureValue(['workflows', 'textWorkflows']) === -1 ? (
										<div className="text-sm text-gray-600 font-medium">Unlimited</div>
									) : (
										<Input
											type="number"
											min="1"
											value={getInputValue(['workflows', 'textWorkflows'])}
											onChange={handleTextWorkflowsInputChange}
											placeholder="Enter number"
											className="w-full"
										/>
									)}
								</div>
							</div>
						)}
					</Card>
				</div>
			</Card>
		</div>
	);
};

export default ClientFeaturesForm;
